import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, CreditCard, Tag, Megaphone, MessageSquare, Settings, LogOut, ArrowLeft, Menu, X, Bell, IndianRupee, ListChecks } from "lucide-react";
import { useAuth } from "@/lib/auth.jsx";
import { LOGO_COLOR } from "@/lib/brand";

const links = [
  { to: "/admin", icon: LayoutDashboard, label: "Overview", end: true },
  { to: "/admin/users", icon: Users, label: "Users" },
  { to: "/admin/plans", icon: IndianRupee, label: "Plans" },
  { to: "/admin/payments", icon: CreditCard, label: "Payments" },
  { to: "/admin/manual-payments", icon: ListChecks, label: "Manual payments" },
  { to: "/admin/coupons", icon: Tag, label: "Coupons" },
  { to: "/admin/announcements", icon: Megaphone, label: "Announcements" },
  { to: "/admin/broadcast", icon: Bell, label: "Broadcast" },
  { to: "/admin/support", icon: MessageSquare, label: "Support" },
  { to: "/admin/settings", icon: Settings, label: "Settings" },
];

function NavBody({ onClick, navigate, onLogout }) {
  return (
    <>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {links.map((l) => (
          <NavLink
            key={l.to} to={l.to} end={l.end} onClick={onClick}
            data-testid={`admin-nav-${l.label.toLowerCase()}`}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition ${
              isActive ? "bg-gradient-warm text-white" : "text-white/70 hover:bg-white/10 hover:text-white"
            }`}
          >
            <l.icon className="w-4 h-4" />
            {l.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-white/10 space-y-1">
        <button onClick={() => { onClick?.(); navigate("/app"); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-white/80 hover:bg-white/10">
          <ArrowLeft className="w-4 h-4" /> Back to App
        </button>
        <button onClick={onLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-white/80 hover:bg-white/10">
          <LogOut className="w-4 h-4" /> Log out
        </button>
      </div>
    </>
  );
}

export default function AdminShell() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const onLogout = () => { logout(); navigate("/"); };

  return (
    <div className="min-h-screen bg-brand-bg flex">
      <aside className="hidden lg:flex w-64 flex-col border-r border-brand-borderc bg-brand-navy text-white sticky top-0 h-screen">
        <div className="p-5 border-b border-white/10">
          <NavLink to="/" className="flex items-center gap-2">
            <img src={LOGO_COLOR} alt="" className="w-9 h-9 rounded-lg" />
            <div>
              <div className="font-bold">DraftVoice</div>
              <div className="text-[10px] uppercase tracking-widest text-brand-pink">Admin</div>
            </div>
          </NavLink>
        </div>
        <NavBody navigate={navigate} onLogout={onLogout} />
      </aside>

      <div className="lg:hidden fixed top-0 inset-x-0 z-40 bg-brand-navy text-white flex items-center justify-between px-4 h-14 pt-[env(safe-area-inset-top)]">
        <button onClick={() => setDrawerOpen(true)} className="p-2 -ml-2" data-testid="admin-mobile-menu">
          <Menu className="w-6 h-6" />
        </button>
        <div className="flex items-center gap-2"><img src={LOGO_COLOR} alt="" className="w-8 h-8 rounded-lg" /><span className="font-bold">Admin</span></div>
        <button onClick={() => navigate("/app")} className="text-xs px-2 py-1 rounded-full bg-white/10">App →</button>
      </div>

      {drawerOpen && (
        <>
          <div className="lg:hidden fixed inset-0 z-50 bg-black/60" onClick={() => setDrawerOpen(false)} />
          <aside className="lg:hidden fixed top-0 left-0 bottom-0 w-72 z-50 bg-brand-navy text-white flex flex-col">
            <div className="p-5 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <img src={LOGO_COLOR} alt="" className="w-9 h-9 rounded-lg" />
                <div>
                  <div className="font-bold">DraftVoice</div>
                  <div className="text-[10px] uppercase tracking-widest text-brand-pink">Admin</div>
                </div>
              </div>
              <button onClick={() => setDrawerOpen(false)} className="p-1.5 hover:bg-white/10 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <NavBody onClick={() => setDrawerOpen(false)} navigate={navigate} onLogout={() => { setDrawerOpen(false); onLogout(); }} />
          </aside>
        </>
      )}

      <main className="flex-1 min-w-0 overflow-x-hidden">
        <div className="lg:hidden h-14" />
        <Outlet />
      </main>
    </div>
  );
}
